import React, { useState } from 'react';
import { UserService } from '../services/supabase';

interface PasswordSetupProps {
  user: any;
  onPasswordSet: () => void;
}

export const PasswordSetup: React.FC<PasswordSetupProps> = ({ user, onPasswordSet }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Validate passwords
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      const userService = new UserService();
      const { error } = await userService.updateUserPassword(password);
      
      if (error) throw error;

      // Update user metadata to mark password as set
      const { supabase } = await import('../services/supabase');
      await supabase.auth.updateUser({
        data: { password_set: true }
      });

      // IMPORTANT: Ensure user schema and tables exist after password is set
      // This ensures the user is fully set up before they can use the app
      const { DataService } = await import('../services/supabase');
      if (user?.email) {
        try {
          console.log('🔧 Ensuring user schema exists after password setup for:', user.email);
          const dataService = new DataService(user.email);
          await dataService.ensureUserSchemaExists();
          console.log('✅ User schema ensured after password setup');
        } catch (schemaError) {
          console.error('❌ Error ensuring user schema after password setup:', schemaError);
          // Don't block password setup, but log the error
        }
      }

      // Clear sessionStorage flag
      sessionStorage.removeItem('supabase_invite_detected');

      // Clear URL parameters after successful password set
      window.history.replaceState({}, document.title, window.location.pathname);
      
      onPasswordSet();
    } catch (err: any) {
      setError(err.message || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="glass-card rounded-xl p-8 shadow-lg">
          {/* Logo and Branding */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-primary shadow-glow flex items-center justify-center">
              <img
                src="/logos/pink_dolphin_logo.png"
                alt="Pink Dolphin logo"
                className="h-10 w-10 object-contain"
              />
            </div>
            <h1 className="text-2xl font-bold text-gradient mb-2">Welcome to Pink Dolphin Tracker!</h1>
            <p className="text-muted-foreground">Please set a password to secure your account</p>
          </div>

          {/* Password Setup Form */}
          <form onSubmit={handleSetPassword} className="space-y-4">
            <div>
              <label htmlFor="email-display" className="block text-sm font-medium text-foreground mb-2">
                Email
              </label>
              <input
                id="email-display"
                type="email"
                value={user.email || ''}
                disabled
                className="w-full px-3 py-2 border border-input rounded-lg bg-muted text-muted-foreground cursor-not-allowed"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                placeholder="Enter your password (min. 6 characters)"
                required
                minLength={6}
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-2">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border border-input rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                placeholder="Confirm your password"
                required
                minLength={6}
              />
            </div>

            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 px-4 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary-dark shadow-md hover:shadow-lg transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Setting Password...' : 'Set Password'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

